import React, { useCallback, useMemo, useState, useRef } from 'react';
import propTypes from 'prop-types';
import { get, noop, isEmpty } from 'lodash';
import { useId } from '@uifabric/react-hooks';
import {
  Callout,
  Text,
  DirectionalHint,
  Spinner,
  SpinnerSize,
  Stack,
  TextField,
  FontIcon,
  CommandButton,
} from '@fluentui/react';
import { useQuery } from 'react-query';
import clsx from 'clsx';
import useDebounce from '../../hooks/useDebounce';
import styles from './FormikTypeaheadPicker.v2.styles';

export const TEXT_FIELD_TEST_ID = 'FormikTypeaheadPickerV2TextField';

const FormikTypeaheadPickerV2 = ({
  field: { name },
  form: { setFieldValue = noop, errors, setFieldTouched = noop },
  loadOptions,
  debounceMs = 500,
  onRenderOption,
  ...props
}) => {
  const error = get(errors, name);
  const textFieldId = useId('formikTypeAheadV2');
  const chevronButtonId = useId('chevronButton');
  const [text, setText] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [debouncedValue] = useDebounce(text, debounceMs, { leading: false, tailing: false });
  const textFieldRef = useRef(null);

  const calloutWidth = textFieldRef.current ? textFieldRef.current.clientWidth : undefined;

  const loadOptionsQuery = useQuery(
    [textFieldId, debouncedValue],
    () => loadOptions(debouncedValue),
    {
      keepPreviousData: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !!debouncedValue && !selectedValue,
    },
  );

  const [userRequestToShowDropdown, setUserRequestToShowDropdown] = useState(undefined);
  const autoShowDropdown = useMemo(
    () => !selectedValue && !!debouncedValue && !loadOptionsQuery.isIdle,
    [debouncedValue, loadOptionsQuery, selectedValue],
  );

  // final computed value of whether to show dropdown or not
  const shouldShowDropdown = useMemo(() => {
    if (typeof userRequestToShowDropdown !== 'undefined') {
      return userRequestToShowDropdown;
    }

    return autoShowDropdown;
  }, [autoShowDropdown, userRequestToShowDropdown]);

  const onItemSelected = useCallback(
    item => {
      setUserRequestToShowDropdown(false);
      setSelectedValue(item.key);
      setText(item.text);
      setFieldValue(name, item.key);
      setFieldTouched(name);
      setHighlightIndex(-1);
    },
    [
      setUserRequestToShowDropdown,
      setSelectedValue,
      setText,
      setFieldValue,
      setFieldTouched,
      setHighlightIndex,
    ],
  );

  const highlightPrevious = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      const listLength = loadOptionsQuery.data?.length;

      let delta = -1;

      if (delta < 0) {
        delta = listLength - 1;
      }
      setHighlightIndex(prevVal => (prevVal + delta) % listLength);
    },
    [loadOptionsQuery.data, setHighlightIndex],
  );

  const highlightNext = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      const listLength = loadOptionsQuery.data?.length;

      const delta = 1;
      setHighlightIndex(prevVal => (prevVal + delta) % listLength);
    },
    [loadOptionsQuery.data, setHighlightIndex],
  );

  const selectHighlightedItem = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      const selectedItem = loadOptionsQuery.data[highlightIndex];

      if (selectedItem) {
        onItemSelected(selectedItem);
      }
    },
    [onItemSelected, loadOptionsQuery.data, highlightIndex],
  );

  const onRenderSuffix = useCallback(() => {
    function toggleShowDropdown() {
      setUserRequestToShowDropdown(!shouldShowDropdown);
    }

    return (
      <Stack horizontal horizontalAlign="center" tokens={{ childrenGap: '0.5em' }}>
        {loadOptionsQuery.isFetching ? (
          <Stack.Item align="center">
            <Spinner size={SpinnerSize.xSmall} />
          </Stack.Item>
        ) : null}

        <Stack.Item align="center">
          <FontIcon
            style={{ cursor: 'pointer' }}
            tabIndex={0}
            role="button"
            id={chevronButtonId}
            onClick={() => toggleShowDropdown()}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                toggleShowDropdown();
              }
            }}
            iconName="ChevronDown"
          />
        </Stack.Item>
      </Stack>
    );
  }, [loadOptionsQuery]);

  const renderDropdown = useCallback(() => {
    if (loadOptionsQuery.isIdle) {
      return null;
    }

    // initial loading the first time
    if (loadOptionsQuery.isLoading) {
      return (
        <Stack style={{ padding: '2em' }} horizontalAlign="center">
          <Text>Loading...</Text>
        </Stack>
      );
    }

    //  error
    if (loadOptionsQuery.isError && loadOptionsQuery.error) {
      return (
        <Stack style={{ padding: '2em' }} horizontalAlign="center">
          <Text>Error Loading Data</Text>
        </Stack>
      );
    }

    // no results
    if (!loadOptionsQuery.isFetching && isEmpty(loadOptionsQuery.data)) {
      return (
        <Stack style={{ padding: '2em' }} horizontalAlign="center">
          <Text>No Results</Text>
        </Stack>
      );
    }

    // render results
    if (loadOptionsQuery.isSuccess && !isEmpty(loadOptionsQuery.data)) {
      return (
        <Stack>
          {loadOptionsQuery.data.map((item, index) => (
            <Stack.Item
              title={item.text}
              key={item.key}
              tabIndex={0}
              role="button"
              className={clsx(highlightIndex === index && styles.optionHighlighted)}
              onClick={() => onItemSelected(item)}
              onKeyDown={() => {}}
            >
              {onRenderOption(item, index)}
            </Stack.Item>
          ))}
        </Stack>
      );
    }

    return null;
  }, [loadOptionsQuery, highlightIndex]);

  return (
    <div>
      <TextField
        elementRef={textFieldRef}
        id={textFieldId}
        data-testid={TEXT_FIELD_TEST_ID}
        placeholder="Search"
        autoComplete="off"
        value={text || ''}
        onRenderSuffix={onRenderSuffix}
        styles={{
          field: {
            fontWeight: 400,
          },
          fieldGroup: {
            backgroundColor: 'transparent',
          },
          prefix: {
            backgroundColor: 'transparent',
          },
          suffix: {
            backgroundColor: 'transparent',
          },
        }}
        {...props}
        onKeyDown={e => {
          if (e.key === 'ArrowUp') {
            highlightPrevious(e);
          } else if (e.key === 'ArrowDown') {
            highlightNext(e);
          } else if (e.key === 'Enter') {
            selectHighlightedItem(e);
          }
        }}
        onChange={(_, newText) => {
          setHighlightIndex(-1);
          setSelectedValue(null);
          setUserRequestToShowDropdown(undefined);
          setText(newText);
        }}
      />
      {error && (
        <div>
          <p className={styles.errorMessage}>
            <span data-automation-id="error-message">
              {typeof error === 'object' && JSON.stringify(error).includes('Required')
                ? 'Required'
                : error}
            </span>
          </p>
        </div>
      )}

      <Callout
        target={`#${textFieldId}`}
        role="list"
        directionalHint={DirectionalHint.bottomLeftEdge}
        isBeakVisible={false}
        gapSpace={2}
        hidden={!shouldShowDropdown}
        calloutMaxHeight={300}
        calloutMinWidth={calloutWidth}
        calloutMaxWidth={calloutWidth}
        calloutWidth={calloutWidth}
        onDismiss={e => {
          // ignore button press on chevron to allow toggling with chevron button
          if (e.target.id === chevronButtonId) {
            return;
          }

          setUserRequestToShowDropdown(false);
        }}
      >
        {renderDropdown()}
      </Callout>
    </div>
  );
};

FormikTypeaheadPickerV2.propTypes = {
  field: propTypes.shape({
    name: propTypes.string.isRequired,
    value: propTypes.any,
    onChange: propTypes.func,
  }).isRequired,
  form: propTypes.shape({
    setFieldValue: propTypes.func,
    setFieldTouched: propTypes.func,
    errors: propTypes.object,
  }).isRequired,
  loadOptions: propTypes.func.isRequired,
  debounceMs: propTypes.number,
  onRenderOption: propTypes.func,
};

FormikTypeaheadPickerV2.defaultProps = {
  debounceMs: 500,
  onRenderOption: item => <CommandButton className={styles.option}>{item.text}</CommandButton>,
};

export default FormikTypeaheadPickerV2;
